import { Helmet } from "react-helmet-async";
import { Button } from "@mineoptics/shared-ui";
import { ArrowRight, Briefcase } from "lucide-react";

export default function Careers() {
  const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.mineoptics.com';

  return <>
    <Helmet>
      <title>Careers | MineOptics</title>
      <meta name="description" content="Join the MineOptics team. Explore career opportunities in mining technology." />
    </Helmet>

    <div className="min-h-screen bg-background">
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
          <a href="/" className="flex items-center gap-2.5 group">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25 transition-transform group-hover:scale-105">M</div>
            <span className="font-semibold text-xl tracking-tight">MineOptics</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/#features" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1">Features</a>
            <a href="/pricing" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1">Pricing</a>
            <a href="https://docs.mineoptics.com" className="nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors pb-1" target="_blank" rel="noopener noreferrer">Docs</a>
          </nav>
          <div className="flex items-center gap-3">
            <Button variant="ghost" className="hidden sm:inline-flex font-medium" asChild>
              <a href={`${APP_URL}/auth/login`}>Sign in</a>
            </Button>
            <Button className="btn-gradient text-white border-0 shadow-lg shadow-primary/25 font-medium" asChild>
              <a href={`${APP_URL}/auth/signup`}>Get Started<ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      </header>

      <section className="relative min-h-[80vh] flex items-center overflow-hidden pt-16">
        <div className="absolute inset-0 gradient-bg opacity-[0.03]" />
        <div className="absolute top-20 right-[10%] w-72 h-72 gradient-bg opacity-20 blob blur-3xl" />
        <div className="relative container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-8">
              <Briefcase className="w-4 h-4" />
              <span>Careers at MineOptics</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight mb-8">
              No Open Positions<br />
              <span className="gradient-text">Right Now</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-10">
              We don't have any open positions at the moment, but we're always looking for talented people.
              Check back soon or reach out to introduce yourself.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" variant="outline" className="font-medium text-base px-8 h-12 bg-white/50 backdrop-blur-sm hover:bg-white/80" asChild>
                <a href="/contact">Get in Touch</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="py-16 md:py-20 bg-muted/30 border-t">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-5 gap-12 md:gap-8">
            <div className="md:col-span-2">
              <a href="/" className="flex items-center gap-2.5 mb-6">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent text-white font-bold text-sm shadow-lg shadow-primary/25">M</div>
                <span className="font-semibold text-xl tracking-tight">MineOptics</span>
              </a>
              <p className="text-muted-foreground max-w-sm leading-relaxed">The modern platform for mining cost modeling.</p>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Product</h3>
              <ul className="space-y-3">
                <li><a href="/#features" className="text-muted-foreground hover:text-primary transition-colors">Features</a></li>
                <li><a href="/pricing" className="text-muted-foreground hover:text-primary transition-colors">Pricing</a></li>
                <li><a href="https://docs.mineoptics.com" className="text-muted-foreground hover:text-primary transition-colors" target="_blank" rel="noopener noreferrer">Documentation</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Company</h3>
              <ul className="space-y-3">
                <li><a href="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</a></li>
                <li><a href="/about" className="text-muted-foreground hover:text-primary transition-colors">About</a></li>
                <li><a href="/careers" className="text-muted-foreground hover:text-primary transition-colors">Careers</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4 text-foreground">Legal</h3>
              <ul className="space-y-3">
                <li><a href="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy</a></li>
                <li><a href="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms</a></li>
                <li><a href="/security" className="text-muted-foreground hover:text-primary transition-colors">Security</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t">
            <p className="text-sm text-muted-foreground text-center">© 2024 MineOptics. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  </>;
}
